// src/App.js
import { useState, useEffect } from 'react';
import { Routes, Route, Outlet, useLocation, Navigate } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { Analytics } from "@vercel/analytics/react";
import { initSmoothScroll } from './utils/smoothScroll';

// Pages
import {AboutPage} from './pages';

// Brand & Product Pages
import {BrandDetailPage, BrandPage, ProductDetailPage} from './pages';

// Feature Pages
import {ContentsPage, TrendPage} from './pages';

// Import ContentDetailPage
import ContentDetailPage from './pages/Contents/ContentDetailPage';

// Import TermsPage and PrivacyPage
import TermsPage from './pages/Terms/TermsPage';
import PrivacyPage from './pages/Privacy/PrivacyPage';

// Components
import MainNav from './components/common/MainNav';
import Footer from './components/common/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import MobileNotice from './components/common/MobileNotice';
// contexts
import { useLanguage } from './contexts/LanguageContext';
// constants
import ROUTES from './constants/routes';
// Styles
import './App.css';

/**
 * 루트 레이아웃 컴포넌트
 * 역할: 전체 애플리케이션의 기본 레이아웃 구조를 정의
 * 구성:
 * - MainNav: 상단 네비게이션 바
 * - Outlet: 라우트에 따른 페이지 컨텐츠
 * - Footer: 하단 푸터
 * - ScrollToTop: 스크롤 제어 컴포넌트
 */
const RootLayout = () => {
  const { language } = useLanguage();

  return (
    <MobileNotice>
      <div className={`app lang-${language}`}>
        <MainNav />
        <main className="contents">
          <Outlet />
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </MobileNotice>
  );
};

/**
 * 메인 App 컴포넌트
 * 역할: 전체 라우팅 구조 정의 및 페이지 전환 관리
 * 
 * 주요 기능:
 * 1. 페이지 전환 시 스크롤 초기화
 * 2. 라우트별 컴포넌트 렌더링
 * 3. 인증 컨텍스트 제공
 * 4. 언어 컨텍스트 제공 (추가됨)
 * 
 * state:
 * - key: 페이지 리렌더링을 위한 키 값
 */
const App = () => {
  const location = useLocation();
  const [key, setKey] = useState(0);

  // Initialize smooth scrolling on mount
  useEffect(() => {
    initSmoothScroll({
      duration: 1000, // Smooth 1-second scroll
      offset: 80, // Offset for fixed header
    });
  }, []);

  // 페이지 전환 시 동작하는 효과
  useEffect(() => {
    if (location.pathname !== '/temp-redirect') {
      setKey(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' }); // Smooth scroll to top
    }
  }, [location.pathname]);

  return (
    <LanguageProvider>
        <Analytics />
        <Routes>
          <Route element={<RootLayout />}>
            {/* 임시 리다이렉트 라우트 */}
            <Route path="/temp-redirect" element={<></>} />
            
            {/* 메인 페이지 라우트 - TrendPage as default */}
            <Route path="/" element={<TrendPage key={key} />} />
            <Route path="/trend" element={<Navigate to="/" replace />} />

            {/* 기능 페이지 라우트 */}
            <Route path={ROUTES.CONTENTS} element={<ContentsPage key={key} />} />
            
            {/* Content detail route */}
            <Route path="/content/:type/:id" element={<ContentDetailPage key={key} />} />
            
            <Route path={ROUTES.ABOUT} element={<AboutPage key={key} />} />
            <Route path={ROUTES.BRAND} element={<BrandPage key={key} />} />
            <Route path={ROUTES.BRAND_DETAIL} element={<BrandDetailPage key={key} />} />
            <Route path={ROUTES.PRODUCT_DETAIL} element={<ProductDetailPage key={key} />} />
            <Route path={ROUTES.TERMS} element={<TermsPage key={key} />} />
            <Route path={ROUTES.PRIVACY} element={<PrivacyPage key={key} />} />
            
            {/* 에러 처리 라우트 */}
            {/* <Route path="/404" element={<NotFoundPage key={key} />} />
            <Route path="*" element={<Navigate to="/404" replace />} /> */}
          </Route>
        </Routes>
    </LanguageProvider>
  );
};

export default App;